import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import {
  ArrowLeft, ArrowRight, Stamp, CheckCircle2, Users, Shield,
  Paintbrush, Settings, Smartphone, Megaphone, XCircle, ImageOff,
  Palette, ShieldAlert, ThumbsUp, Sparkles, MousePointerClick,
  LayoutTemplate, BarChart3, RefreshCw, UserCog, FolderSync } from
"lucide-react";
import { motion } from "framer-motion";
import emailHeroBg from "@/assets/email-handtekeningen-hero-bg.jpg";

const features = [
"Je eigen online portaal met drag-and-drop editor",
"Kies uit professioneel ontworpen templates of maak je eigen ontwerp",
"Contactgegevens worden automatisch gesynchroniseerd met Microsoft 365",
"Automatisch toegepast op alle uitgaande e-mails — ook op mobiel en webmail",
"Afdeling- of functiespecifieke handtekeningen mogelijk",
"Plan campagnebanners in en meet klikgedrag met analytics",
"Ondersteuning voor logo's, foto's, sociale media-iconen en klikbare links",
"Altijd consistent, ook bij antwoorden en doorsturen",
"Rollen en rechten: geef Marketing of HR toegang om zelf handtekeningen aan te passen",
"Naadloze integratie met Microsoft 365 en Azure Active Directory",
"ISO 27001 gecertificeerd, AVG-compliant en 99,99% uptime"];


const highlights = [
{
  icon: MousePointerClick,
  title: "Jouw eigen portaal",
  text: "Je krijgt een eigen online omgeving waar je handtekeningen ontwerpt, beheert en uitrolt. Met een simpele drag-and-drop editor maak je in minuten een professioneel ontwerp.",
  color: "bg-hero-gradient"
},
{
  icon: LayoutTemplate,
  title: "Kant-en-klare templates",
  text: "Geen inspiratie? Kies uit tientallen professioneel ontworpen templates en pas ze aan met je eigen logo, kleuren en contactgegevens.",
  color: "bg-rinqel-blue-gradient"
},
{
  icon: FolderSync,
  title: "Auto-sync met Microsoft 365",
  text: "Naam, functie, telefoonnummer — alles wordt automatisch opgehaald uit Azure Active Directory. Gegevens altijd up-to-date, zonder handmatig werk.",
  color: "bg-rinqel-green-gradient"
},
{
  icon: UserCog,
  title: "Rollen en rechten",
  text: "Geef Marketing toegang om banners te beheren, of laat HR de vacaturetekst aanpassen. Iedereen werkt in hetzelfde portaal, met precies de juiste rechten.",
  color: "bg-hero-gradient"
},
{
  icon: BarChart3,
  title: "Campagnes met analytics",
  text: "Plan campagnebanners in voor specifieke periodes en meet hoeveel mensen erop klikken. Zo wordt elke e-mail een marketingkanaal met meetbare resultaten.",
  color: "bg-rinqel-blue-gradient"
},
{
  icon: Shield,
  title: "Veilig en gecertificeerd",
  text: "ISO 27001, SOC II Type 2, AVG-compliant en 99,99% uptime. Je e-mailhandtekeningen draaien op een platform dat door meer dan 9 miljoen accounts wereldwijd wordt gebruikt.",
  color: "bg-rinqel-green-gradient"
}];


const WerkplekEmailHandtekeningen = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[420px] overflow-hidden bg-black">
        <img loading="eager" decoding="async" fetchPriority="high" src={emailHeroBg} alt="Professionele e-mailhandtekeningen voor je hele organisatie" className="absolute inset-0 h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
        <div className="container relative z-10 flex min-h-[420px] items-center py-20">
          <div className="max-w-2xl">
            <Link
              to="/diensten/werkplek"
              className="mb-6 inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors">
              
              <ArrowLeft className="h-4 w-4" />
              Terug naar Digitale Werkplek
            </Link>

            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-white" />
                Optionele dienst
              </span>
            </motion.div>

            <motion.div
              className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.05 }}>
              
              <Stamp className="h-7 w-7 text-white" />
            </motion.div>

            <motion.h1
              className="text-4xl font-bold leading-[1.1] tracking-tight text-white md:text-5xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}>
              
              Centrale{" "}
              <span className="text-white/90">e-mailhandtekeningen</span>
            </motion.h1>

            <motion.p
              className="mt-5 max-w-xl text-lg leading-relaxed text-white/80"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}>
              
              Professionele, uniforme e-mailhandtekeningen voor je hele bedrijf. Met je eigen online portaal, drag-and-drop editor en automatische uitrol op elk apparaat.
            </motion.p>

            <motion.ul
              className="mt-6 space-y-2"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}>
              
              {[
              "Eigen online portaal met drag-and-drop editor",
              "Automatisch gesynchroniseerd met Microsoft 365",
              "Werkt op desktop, mobiel en webmail — altijd correct",
              "Inclusief campagnebanners, analytics en rollen/rechten"].
              map((item) =>
              <li key={item} className="flex items-center gap-2 text-sm text-white/80">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  {item}
                </li>
              )}
            </motion.ul>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}>
            
            <h2 className="text-2xl font-bold md:text-3xl">
              Elke e-mail is een <span className="text-gradient">visitekaartje</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Je stuurt dagelijks tientallen e-mails. Iedere e-mail is een kans om professioneel over te komen.
              Met onze oplossing krijg je een eigen online portaal waar je handtekeningen ontwerpt met een simpele drag-and-drop editor.
              Contactgegevens worden automatisch opgehaald uit Microsoft 365 — dus altijd up-to-date, op elk apparaat.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Probleem zonder oplossing */}
      <section className="bg-muted/40 py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}>
            
            <h2 className="text-2xl font-bold md:text-3xl">
              Zonder centrale handtekeningen?{" "}
              <span className="text-gradient">Dan herken je dit vast</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Veel bedrijven laten e-mailhandtekeningen over aan medewerkers zelf.
              Op een laptop lukt dat nog redelijk — maar op een smartphone of tablet? Daar gaat het mis.
            </p>
          </motion.div>

          <div className="mx-auto mt-12 grid max-w-5xl gap-5 md:grid-cols-2">
            {[
            {
              icon: ImageOff,
              title: "Kapotte logo's en afbeeldingen",
              text: "Je logo als bijlage in plaats van in de handtekening? Ontvangers zien een rood kruisje of een losse bijlage genaamd 'image001.png'. Niet bepaald professioneel.",
              color: "bg-destructive/10 text-destructive"
            },
            {
              icon: Smartphone,
              title: "Mobiel? Geen handtekening",
              text: "Op een telefoon of tablet wordt de handtekening vaak helemaal niet meegestuurd. Of er staat alleen 'Verzonden vanaf mijn iPhone' — zonder naam, nummer of logo.",
              color: "bg-destructive/10 text-destructive"
            },
            {
              icon: Palette,
              title: "Iedereen z'n eigen stijl",
              text: "De ene collega gebruikt Comic Sans, de ander vergeet z'n telefoonnummer en weer een ander heeft het oude logo. Elke e-mail ziet er anders uit.",
              color: "bg-destructive/10 text-destructive"
            },
            {
              icon: ShieldAlert,
              title: "Geen disclaimer of privacyverklaring",
              text: "Wettelijk ben je soms verplicht een disclaimer mee te sturen. Als medewerkers dat zelf moeten regelen, gaat het gegarandeerd fout.",
              color: "bg-destructive/10 text-destructive"
            }].
            map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="flex gap-4 rounded-xl border border-border bg-card p-6 shadow-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}>
                  
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${item.color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{item.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                  </div>
                </motion.div>);

            })}
          </div>

          {/* Wat lost het op */}
          <motion.div
            className="mx-auto mt-16 max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}>
            
            <h2 className="text-2xl font-bold md:text-3xl">
              Met centrale handtekeningen?{" "}
              <span className="text-gradient">Alles opgelost</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Wij regelen het centraal. Jouw medewerkers hoeven er niets voor te doen — en elke e-mail ziet er professioneel uit. Altijd. Overal.
            </p>
          </motion.div>

          <div className="mx-auto mt-10 grid max-w-5xl gap-5 md:grid-cols-2">
            {[
            {
              icon: Sparkles,
              title: "Altijd professioneel, op elk apparaat",
              text: "Of je collega nu mailt vanaf een laptop, telefoon of tablet — de handtekening wordt automatisch toegevoegd. Met logo, kleuren en contactgegevens. Altijd correct.",
              color: "bg-rinqel-green-gradient text-primary-foreground"
            },
            {
              icon: ThumbsUp,
              title: "Geen gedoe voor medewerkers",
              text: "Niemand hoeft zelf een handtekening in te stellen. Nieuwe medewerker? Automatisch de juiste handtekening. Telefoonnummer gewijzigd? Eén keer aanpassen, overal bijgewerkt.",
              color: "bg-rinqel-green-gradient text-primary-foreground"
            },
            {
              icon: Shield,
              title: "Juridisch in orde",
              text: "Disclaimers en privacyverklaringen worden automatisch meegestuurd. Zo voldoe je zonder moeite aan de AVG en andere wettelijke eisen.",
              color: "bg-rinqel-green-gradient text-primary-foreground"
            },
            {
              icon: Megaphone,
              title: "Marketing in elke e-mail",
              text: "Voeg een klikbare campagnebanner toe aan de handtekening van je hele team. Zo wordt elke e-mail ook een marketingkanaal — zonder extra kosten.",
              color: "bg-rinqel-green-gradient text-primary-foreground"
            }].
            map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="flex gap-4 rounded-xl border border-border bg-card p-6 shadow-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}>
                  
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${item.color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{item.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                  </div>
                </motion.div>);

            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.h2
            className="mb-12 text-center text-2xl font-bold md:text-3xl"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}>
            
            Wat zit erin?
          </motion.h2>

          <motion.ul
            className="mx-auto max-w-2xl space-y-3"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}>
            
            {features.map((f) =>
            <li key={f} className="flex items-start gap-3 text-sm leading-relaxed">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-rinqel-green" />
                {f}
              </li>
            )}
          </motion.ul>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.h2
            className="mb-12 text-center text-2xl font-bold md:text-3xl"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}>
            
            Professioneel en{" "}
            <span className="text-gradient">praktisch</span>
          </motion.h2>

          <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-2">
            {highlights.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="flex gap-4 rounded-xl border border-border bg-card p-6 shadow-card transition-shadow hover:shadow-card-hover"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}>
                  
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${item.color} text-primary-foreground`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{item.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                  </div>
                </motion.div>);

            })}
          </div>
        </div>
      </section>

      {/* Hoe werkt het */}
      <section className="bg-muted/40 py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}>
            
            <h2 className="text-2xl font-bold md:text-3xl">
              Hoe werkt het? <span className="text-gradient">4 simpele stappen</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Je hoeft geen IT-expert te zijn. Wij richten alles in — jij kiest hoe je handtekeningen eruitzien.
            </p>
          </motion.div>

          <div className="mx-auto mt-12 grid max-w-4xl gap-6 md:grid-cols-2">
            {[
            {
              step: "1",
              title: "Wij richten je account in",
              text: "We koppelen het platform aan jullie Microsoft 365-omgeving. Alle medewerkers, afdelingen en contactgegevens worden automatisch gesynchroniseerd."
            },
            {
              step: "2",
              title: "Ontwerp je handtekening",
              text: "Kies een template of bouw je eigen ontwerp met de drag-and-drop editor. Logo, kleuren, sociale media, foto — alles sleep je op z'n plek."
            },
            {
              step: "3",
              title: "Stel regels in",
              text: "Bepaal wie welke handtekening krijgt. Per afdeling, functie of zelfs per persoon. Voeg een campagnebanner of disclaimer toe als je wilt."
            },
            {
              step: "4",
              title: "Klaar — het werkt automatisch",
              text: "Elke e-mail die je bedrijf verlaat, krijgt automatisch de juiste handtekening. Op laptop, telefoon en webmail. Medewerkers hoeven niets te doen."
            }].
            map((item, i) =>
            <motion.div
              key={item.step}
              className="flex gap-4 rounded-xl border border-border bg-card p-6 shadow-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}>
              
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-hero-gradient text-primary-foreground font-bold text-lg">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-semibold">{item.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Prijs */}
      <section className="py-12">
        <div className="container">
          <motion.div
            className="mx-auto max-w-2xl rounded-xl border border-border bg-card p-6 md:p-8 shadow-card text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}>
            
            <h3 className="text-lg font-bold">Wat kost het?</h3>
            <div className="mt-4 flex items-baseline justify-center gap-1">
              <span className="text-3xl font-bold">€ 1,35</span>
              <span className="text-muted-foreground">per maand per gebruiker</span>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Minimale afname van 10 gebruikers per Microsoft-omgeving. Daarna per stuk uitbreidbaar (11, 12, 13, etc.).
            </p>
            <div className="mt-5">
              <Button variant="hero" asChild>
                <TrackedLink to="/contact" trackLabel="Neem contact op" trackLocation="werkplek-emailhandtekeningen-prijs">
                  Meer weten? Neem contact op
                  <ArrowRight className="ml-1 h-4 w-4" />
                </TrackedLink>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-muted/40 py-12">
        <div className="container">
          <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:justify-center">
            <Button variant="hero" size="lg" asChild>
              <TrackedLink to="/contact" trackLabel="Neem contact op" trackLocation="werkplek-emailhandtekeningen-cta">
                Meer weten? Neem contact op
                <ArrowRight className="ml-1 h-4 w-4" />
              </TrackedLink>
            </Button>
            <Button variant="hero-outline" size="lg" asChild>
              <Link to="/diensten/werkplek">Terug naar overzicht</Link>
            </Button>
          </div>
        </div>
      </section>
    </>);

};

export default WerkplekEmailHandtekeningen;